import React from 'react';
import {
    Alert,
    Button,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Nav,
    NavItem,
    NavLink,
} from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCheckSquare,
    faInfoCircle,
    faLink,
    faSave,
    faSquare,
    faTimes,
    faTrashAlt,
} from '@fortawesome/free-solid-svg-icons';
import RemoveAssignmentsOrderSummary from './RemoveAssignmentsOrderSummary';
import RemoveAssignmentsEquipmentLabel from './RemoveAssignmentsEquipmentLabel';

export default class ManageAssignmentsModal extends React.Component {
    static defaultProps = { isOpen: true };

    constructor(props) {
        super(props);

        this.state = {
            employeeSelected: true,
            selectedEquipmentIds: props.equipment.map((x) => x.jobAssignmentId),
            selectedDetachIds: [],
            confirmed: false,
            activeTab: 'remove',
            lockedWorkflow: null,
        };
    }

    onTabChange = (activeTab) => {
        if (!this.state.lockedWorkflow) {
            this.setState({ activeTab });
        }
    };

    onEmployeeChange = () => {
        const { equipment } = this.props;

        if (!this.state.employeeSelected) {
            this.setState({
                employeeSelected: true,
                selectedEquipmentIds: equipment.map((x) => x.jobAssignmentId),
                lockedWorkflow: 'remove',
            });
        } else {
            this.setState({
                employeeSelected: false,
                confirmed: false,
                lockedWorkflow: 'remove',
            });
        }
    };

    onEquipmentChange = (jobAssignmentId, checked) => {
        this.setState(({ selectedEquipmentIds }) => ({
            selectedEquipmentIds: checked
                ? [...selectedEquipmentIds, jobAssignmentId]
                : selectedEquipmentIds.filter((id) => id !== jobAssignmentId),
            lockedWorkflow: 'remove',
        }));
    };

    onDetachChange = (jobAssignmentId, checked) => {
        this.setState(({ selectedDetachIds }) => ({
            selectedDetachIds: checked
                ? [...selectedDetachIds, jobAssignmentId]
                : selectedDetachIds.filter((id) => id !== jobAssignmentId),
            lockedWorkflow: 'update',
        }));
    };

    onConfirmToggle = () =>
        this.setState(({ confirmed }) => ({
            confirmed: !confirmed,
            lockedWorkflow: 'remove',
        }));

    canSave = () => {
        const {
            activeTab,
            employeeSelected,
            selectedEquipmentIds,
            selectedDetachIds,
            confirmed,
        } = this.state;

        if (activeTab === 'update') {
            return selectedDetachIds.length > 0;
        }

        const hasSelection =
            employeeSelected || selectedEquipmentIds.length > 0;

        return hasSelection && (!employeeSelected || confirmed);
    };

    onSaveClick = () => {
        const { employee, equipment, equipmentToUpdate, onSave, onUpdateSave } =
            this.props;
        const {
            activeTab,
            employeeSelected,
            selectedEquipmentIds,
            selectedDetachIds,
        } = this.state;

        if (activeTab === 'update') {
            onUpdateSave(
                equipmentToUpdate
                    .filter((x) =>
                        selectedDetachIds.includes(x.jobAssignmentId)
                    )
                    .map((x) => ({
                        jobAssignmentId: x.jobAssignmentId,
                        clearEquipmentEmployeeAssignmentId: true,
                        clearEquipmentAssignedEquipmentId: true,
                    }))
            );
            return;
        }

        onSave([
            ...(employeeSelected ? [employee] : []),
            ...equipment.filter((x) =>
                selectedEquipmentIds.includes(x.jobAssignmentId)
            ),
        ]);
    };

    isEquipmentChecked = (equipment) => {
        const { employeeSelected, selectedEquipmentIds } = this.state;

        return (
            employeeSelected ||
            selectedEquipmentIds.includes(equipment.jobAssignmentId)
        );
    };

    renderToggleButton = ({ selected, disabled, onClick, children }) => (
        <Button
            block
            outline={!selected}
            color={selected ? 'danger' : 'secondary'}
            className="text-left d-flex align-items-center mb-2"
            style={disabled ? { opacity: 1, cursor: 'not-allowed' } : undefined}
            disabled={disabled}
            onClick={onClick}
        >
            <FontAwesomeIcon
                icon={selected ? faCheckSquare : faSquare}
                className="mr-2"
            />
            <span>{children}</span>
        </Button>
    );

    renderEquipment = (equipment) => (
        <React.Fragment key={equipment.jobAssignmentId}>
            {this.renderToggleButton({
                selected: this.isEquipmentChecked(equipment),
                disabled: this.state.employeeSelected,
                onClick: () =>
                    this.onEquipmentChange(
                        equipment.jobAssignmentId,
                        !this.isEquipmentChecked(equipment)
                    ),
                children: (
                    <RemoveAssignmentsEquipmentLabel equipment={equipment} />
                ),
            })}
        </React.Fragment>
    );

    renderRemoveTab = () => {
        const { employee, equipment } = this.props;
        const { employeeSelected, confirmed } = this.state;

        return (
            <>
                <table className="table table-sm mb-3">
                    <thead>
                        <tr>
                            <th style={{ width: '40%' }}>Employee</th>
                            <th>Equipment</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td style={{ verticalAlign: 'top' }}>
                                {this.renderToggleButton({
                                    selected: employeeSelected,
                                    onClick: this.onEmployeeChange,
                                    children: employee.assignment?.name,
                                })}
                            </td>
                            <td>{equipment.map(this.renderEquipment)}</td>
                        </tr>
                    </tbody>
                </table>

                {employeeSelected && !this.canSave() && (
                    <Alert color="info" fade={false} className="d-flex">
                        <div className="mr-2">
                            <FontAwesomeIcon icon={faInfoCircle} />
                        </div>
                        <div>
                            If you wish to remove all assignments, please click
                            the button below. Otherwise, deselect the employee
                            to deselect individual assignments.
                        </div>
                    </Alert>
                )}

                {employeeSelected && (
                    <Button
                        outline
                        block
                        color="danger"
                        active={confirmed}
                        onClick={this.onConfirmToggle}
                    >
                        {confirmed
                            ? 'Removal of this employee is enabled'
                            : 'Click to enable removal of this employee and all their equipment assignments'}
                    </Button>
                )}
            </>
        );
    };

    renderUpdateTab = () => {
        const { equipmentToUpdate } = this.props;
        const { selectedDetachIds } = this.state;
        const linkedEquipment = equipmentToUpdate.filter(
            (x) =>
                x.assignedEmployee?.id != null ||
                x.assignedEquipment?.id != null
        );

        return (
            <>
                <Alert color="info" fade={false} className="d-flex">
                    <div className="mr-2">
                        <FontAwesomeIcon icon={faInfoCircle} />
                    </div>
                    <div>
                        Select equipment to detach it from its assigned employee
                        and linked equipment. The equipment assignment remains
                        on this order.
                    </div>
                </Alert>
                <table className="table table-sm mb-3">
                    <thead>
                        <tr>
                            <th>Equipment assignment</th>
                            <th>Detach associations</th>
                        </tr>
                    </thead>
                    <tbody>
                        {linkedEquipment.map((item) => (
                            <tr key={item.jobAssignmentId}>
                                <td style={{ verticalAlign: 'top' }}>
                                    <RemoveAssignmentsEquipmentLabel
                                        equipment={item}
                                    />
                                </td>
                                <td>
                                    {this.renderToggleButton({
                                        selected: selectedDetachIds.includes(
                                            item.jobAssignmentId
                                        ),
                                        onClick: () =>
                                            this.onDetachChange(
                                                item.jobAssignmentId,
                                                !selectedDetachIds.includes(
                                                    item.jobAssignmentId
                                                )
                                            ),
                                        children:
                                            'Detach from employee / unlink from equipment',
                                    })}
                                </td>
                            </tr>
                        ))}
                        {linkedEquipment.length === 0 && (
                            <tr>
                                <td colSpan="2">
                                    No equipment assignment links are available
                                    to clear.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </>
        );
    };

    renderTabs = () => {
        const { activeTab } = this.state;
        const tabs = [
            {
                id: 'remove',
                icon: faTrashAlt,
                label: 'Remove Assignments',
                description: 'Remove resources from this order',
            },
            {
                id: 'update',
                icon: faLink,
                label: 'Update Assignments',
                description: 'Detach equipment without removing it',
            },
        ];

        return (
            <>
                <div className="font-weight-bold mb-2">
                    Choose an assignment action
                </div>
                <Nav tabs className="nav-fill mb-3" role="tablist">
                    {tabs.map((tab) => {
                        const selected = activeTab === tab.id;

                        return (
                            <NavItem key={tab.id} className="flex-fill">
                                <NavLink
                                    id={`${tab.id}-assignments-tab`}
                                    role="tab"
                                    aria-selected={selected}
                                    aria-controls="assignments-tab-panel"
                                    active={selected}
                                    className="d-flex align-items-center justify-content-center h-100 text-center"
                                    style={{
                                        minHeight: '76px',
                                        borderBottom: selected
                                            ? '4px solid #007bff'
                                            : '4px solid transparent',
                                        backgroundColor: selected
                                            ? '#eaf3ff'
                                            : '#f8f9fa',
                                        cursor: 'pointer',
                                    }}
                                    onClick={() => this.onTabChange(tab.id)}
                                >
                                    <FontAwesomeIcon
                                        icon={tab.icon}
                                        size="lg"
                                        className="mr-2"
                                    />
                                    <span>
                                        <strong className="d-block">
                                            {tab.label}
                                        </strong>
                                        <small>{tab.description}</small>
                                    </span>
                                </NavLink>
                            </NavItem>
                        );
                    })}
                </Nav>
            </>
        );
    };

    renderLockedWorkflow = () => {
        const isRemoveWorkflow = this.state.lockedWorkflow === 'remove';

        return (
            <Alert
                color={isRemoveWorkflow ? 'danger' : 'info'}
                fade={false}
                className="d-flex align-items-center"
                role="status"
            >
                <FontAwesomeIcon
                    icon={isRemoveWorkflow ? faTrashAlt : faLink}
                    className="mr-2"
                />
                <span>
                    <strong>
                        {isRemoveWorkflow
                            ? 'Removal workflow selected.'
                            : 'Equipment detach workflow selected.'}
                    </strong>{' '}
                    Save or cancel to finish this action.
                </span>
            </Alert>
        );
    };

    render() {
        const { job, day, onCancel, isOpen } = this.props;
        const { activeTab, lockedWorkflow } = this.state;

        return (
            <Modal isOpen={isOpen} toggle={onCancel} size="xl">
                <ModalHeader toggle={onCancel}>Manage Assignments</ModalHeader>
                <ModalBody>
                    <RemoveAssignmentsOrderSummary job={job} day={day} />
                    {!lockedWorkflow
                        ? this.renderTabs()
                        : this.renderLockedWorkflow()}
                    <div
                        id="assignments-tab-panel"
                        role={lockedWorkflow ? undefined : 'tabpanel'}
                        aria-labelledby={
                            lockedWorkflow
                                ? undefined
                                : `${activeTab}-assignments-tab`
                        }
                        tabIndex={lockedWorkflow ? undefined : 0}
                    >
                        {activeTab === 'remove'
                            ? this.renderRemoveTab()
                            : this.renderUpdateTab()}
                    </div>
                </ModalBody>
                <ModalFooter className="d-flex w-100">
                    <div className="w-25 mr-auto">
                        <Button
                            color="warning"
                            onClick={onCancel}
                            className="w-100"
                        >
                            <FontAwesomeIcon icon={faTimes} className="mr-2" />
                            Cancel
                        </Button>
                    </div>
                    <div className="w-50 text-right">
                        <Button
                            color="danger"
                            disabled={!this.canSave()}
                            outline={!this.canSave()}
                            onClick={this.onSaveClick}
                            className="w-100"
                        >
                            <FontAwesomeIcon icon={faSave} className="mr-2" />
                            Save
                        </Button>
                    </div>
                </ModalFooter>
            </Modal>
        );
    }
}
